import React from 'react';

import { Field } from '@/design-system/field';
import { Hint } from '@/design-system/hint';
import { Label } from '@/design-system/label';

import MoneyInput from './MoneyInput';

/**
 * Biaya operasional as six fixed fields. One editor for both places costs are
 * typed: the Route Planner's "Biaya rute" and the UJP wizard's step Biaya
 * (pre-filled from the picked route).
 *
 * The wire stays dynamic (`costItems: { label, amount }[]`,
 * PLAN-UJP-DYNAMIC-COSTS); only the form is locked to these slots. Each slot
 * sends its canonical `label` — the same names migration 0113 backfilled the
 * old fixed columns into — so the server's name-based payment rule (Flazz for
 * toll / parkir tap when the vehicle has Flazz, else transfer) keeps working,
 * and old UJPs land back in their own slot. A row whose name matches no slot
 * (typed while the form was free-form) is folded into "Biaya lain-lain"; its
 * name goes into the note via `foldedCostNote` so it is not lost.
 *
 * Co-located with `MoneyInput` in `pages/ujp/components/`, like the money
 * inputs it is built from; the planner imports it from here.
 */

export interface CostSlot {
  key: string;
  /** Sent as the row's `label`; also what an existing row is matched on. */
  label: string;
  /** Field label in the form. */
  display: string;
}

export const COST_SLOTS: readonly CostSlot[] = [
  { key: 'toll', label: 'Toll', display: 'Toll (Flazz)' },
  { key: 'parkir-tap', label: 'Parkir tap', display: 'Parkir tap machine' },
  { key: 'parkir-manual', label: 'Parkir manual', display: 'Parkir manual' },
  { key: 'bongkar-muat', label: 'Biaya bongkar muat', display: 'Biaya bongkar muat' },
  { key: 'uang-makan', label: 'Uang makan', display: 'Uang makan' },
  { key: 'lain-lain', label: 'Biaya lain-lain', display: 'Biaya lain-lain' },
];

const OTHER_SLOT = COST_SLOTS[COST_SLOTS.length - 1];

/** One slot's value; `amount` is digits only (what `MoneyInput` produces). */
export interface CostItemDraft {
  /** `cost-<slot key>` — the wizard keys step errors by the `cost-` prefix. */
  uid: string;
  label: string;
  amount: string;
}

/** A row as the API takes it (route plan item / UJP cost item). */
export interface CostItemValue {
  label: string;
  amount: string;
}

const normalize = (s: string) => s.trim().toLowerCase().replace(/\s+/g, ' ');

const slotOf = (label: string): CostSlot | undefined =>
  COST_SLOTS.find(
    (s) =>
      normalize(s.label) === normalize(label) ||
      normalize(s.display) === normalize(label)
  );

const rupiah = (amount: string) => Math.round(Number(amount) || 0);

/** The six slots, all empty. */
export const emptyCostDrafts = (): CostItemDraft[] =>
  COST_SLOTS.map((s) => ({ uid: `cost-${s.key}`, label: s.label, amount: '' }));

/**
 * Server rows → the six slots. Rows are summed into the slot their name
 * matches; anything else goes to "Biaya lain-lain". The editor is whole-rupiah.
 */
export const toCostDrafts = (items: CostItemValue[]): CostItemDraft[] => {
  const totals = new Map<string, number>();
  items.forEach((item) => {
    const slot = slotOf(item.label) ?? OTHER_SLOT;
    totals.set(slot.key, (totals.get(slot.key) ?? 0) + rupiah(item.amount));
  });
  return emptyCostDrafts().map((d, i) => {
    const total = totals.get(COST_SLOTS[i].key) ?? 0;
    return total > 0 ? { ...d, amount: String(total) } : d;
  });
};

/** Names of the non-zero rows that matched no slot (folded into lain-lain). */
export const unmatchedCostLabels = (items: CostItemValue[]): string[] =>
  Array.from(
    new Set(
      items
        .filter((item) => rupiah(item.amount) > 0 && !slotOf(item.label))
        .map((item) => item.label.trim())
        .filter(Boolean)
    )
  );

/**
 * The note with the folded row names appended ("Biaya lain-lain termasuk: …"),
 * once — re-applying the same rows leaves the note as it is.
 */
export const foldedCostNote = (note: string, items: CostItemValue[]): string => {
  const names = unmatchedCostLabels(items);
  if (names.length === 0) return note;
  const line = `Biaya lain-lain termasuk: ${names.join(', ')}`;
  if (note.includes(line)) return note;
  return note.trim() ? `${note.trim()}\n${line}` : line;
};

/** The slots → the payload the API takes. Empty / zero slots are not sent. */
export const toCostItemsPayload = (rows: CostItemDraft[]): CostItemValue[] =>
  rows
    .filter((r) => rupiah(r.amount) > 0)
    .map((r) => ({ label: r.label, amount: String(rupiah(r.amount)) }));

/** Σ amounts of the rows (or of server items), in rupiah. */
export const costTotal = (rows: Array<{ amount: string }>): number =>
  rows.reduce((sum, r) => sum + (Number(r.amount) || 0), 0);

export interface CostItemsEditorProps {
  value: CostItemDraft[];
  onChange: (rows: CostItemDraft[]) => void;
  disabled?: boolean;
  /** Shown under a slot, e.g. "Dibayar: Flazz". */
  rowHint?: (row: CostItemDraft) => React.ReactNode;
}

const CostItemsEditor: React.FC<CostItemsEditorProps> = ({
  value,
  onChange,
  disabled,
  rowHint,
}) => {
  const patch = (uid: string, amount: string) =>
    onChange(value.map((r) => (r.uid === uid ? { ...r, amount } : r)));

  return (
    <div
      className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
      data-testid="cost-items"
    >
      {value.map((row) => {
        const slot = slotOf(row.label);
        const hint = rowHint?.(row);
        return (
          <Field key={row.uid} data-testid="cost-item-row">
            <Label htmlFor={`cost-amount-${row.uid}`}>
              {slot?.display ?? row.label}
            </Label>
            <MoneyInput
              id={`cost-amount-${row.uid}`}
              value={row.amount}
              disabled={disabled}
              onChange={(digits) => patch(row.uid, digits)}
            />
            {hint && <Hint data-testid="cost-item-hint">{hint}</Hint>}
          </Field>
        );
      })}
    </div>
  );
};

export default CostItemsEditor;
